/**
 * Query list (WS-1) — the master list of the CURRENT page's queries, shown in
 * the left rail's "Query" context. Selecting a query opens/activates its tab in
 * the main workspace; the detail editor lives in `QueryEditorPane`, not here
 * (deletion is the editor's "remove" button, guarded by the parent). Add seeds a
 * `table` query and opens it.
 */
import { Plus } from "lucide-react";

import type { PageQuery } from "@/lib/pageTypes";
import type { PageReferences, Usage } from "@/lib/pageReferences";

import { UsageBadge } from "./UsageBadge";

/** Short source label for the list row. */
function sourceLabel(q: PageQuery): string {
  const k = q.source_kind ?? "table";
  return k === "table" ? "Tabella" : k === "dataset" ? "Proiezione" : k.toUpperCase();
}

export function QueryListPanel({
  queries,
  activeId,
  canAdd,
  onSelect,
  onAdd,
  references,
  onJump,
}: {
  queries: PageQuery[];
  /** The query whose tab is currently active (highlighted), or null. */
  activeId: string | null;
  /** False when the app has no tables yet (a `table` query can't be seeded). */
  canAdd: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
  references?: PageReferences;
  onJump?: (u: Usage) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onAdd}
        disabled={!canAdd}
        className="mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuova query
      </button>

      {queries.length === 0 ? (
        <p className="px-1 py-2 text-[11px] text-muted-foreground">
          {canAdd ? "Nessuna query. + per iniziare." : "Aggiungi prima una tabella."}
        </p>
      ) : (
        queries.map((q) => {
          const active = q.id === activeId;
          return (
            <div
              key={q.id}
              className={`flex items-center gap-1 rounded-md pr-1 ${
                active ? "bg-accent/15" : "hover:bg-surface"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(q.id)}
                className={`flex min-w-0 flex-1 items-center justify-between gap-1 px-2 py-1.5 text-left text-[12px] ${
                  active ? "text-accent" : "text-foreground"
                }`}
              >
                <span className="min-w-0 flex-1 truncate font-medium">{q.name}</span>
                <span className="shrink-0 text-[9px] uppercase text-muted-foreground/70">
                  {sourceLabel(q)}
                </span>
              </button>
              {references && (
                <UsageBadge
                  usages={references.query[q.name] ?? []}
                  hasOpaque={references.hasOpaque}
                  onJump={(u) => onJump?.(u)}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
